import { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Plus, Settings2, Map as MapIcon, SearchX, CircleHelp } from "lucide-react";

import { readActivityFilter, activityQuery, accessChoiceCount, filterActivities, mapEntries, seriesOf, ACCESS_CHOICES, FILTER_FEATURES } from "@/lib/v3Activities";
import { useMemberAuth } from "../MemberAuthContext";
import { useCore } from "../useCore";
import { requiredCollection } from "../requiredCollection";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, Unavailable } from "../components/kit";
import { V3Title, V3Columns, V3Actions, StatusBanner, DetailRow, DetailList, AsideCard, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { SearchField, FilterBar, Pill, AreaCard, ResponsibleCard, V3_GHOST, V3_CARD } from "../components/v3app";
import { SelectCard } from "../components/v3pass";
import { ActivityListCard, activityHref } from "../components/v3act";

const hasActivityId = (activity) => (typeof activity.id === "string" && activity.id.length > 0)
  || (typeof activity.id === "number" && Number.isFinite(activity.id));
const useActivities = () => useCore("/member/activities?category=all", (data) =>
  requiredCollection(data, "activities").filter(hasActivityId));

function NoMatch({ f, city, onBack }) {
  const n = accessChoiceCount(f);
  return (
    <div data-m="M118" data-testid="activities-no-match">
      <StatusBanner tone="sand" icon={SearchX} title="Keine passenden Angebote" text={city || "Alle Orte"} testid="activities-empty" />
      <DetailList>
        <DetailRow label="Region" value={city || "Alle Orte"} testid="activities-empty-region" />
        <DetailRow label="Suche" value={f.q ? `„${f.q}“` : "Keine Suche"} testid="activities-empty-q" />
        <DetailRow label="Zugangsfilter" value={n ? `${n} aktive${n === 1 ? "r" : ""} Filter` : "Keine Filter"} testid="activities-empty-access" />
        <DetailRow label="Nächster Schritt" value="Filter ändern oder zurücksetzen" testid="activities-empty-next" />
      </DetailList>
      <V3Actions>
        <Link to={`/aktivitaeten/zugang${activityQuery(f)}`} data-testid="activities-empty-change" className={V3_PRIMARY}>Filter ändern</Link>
        <button type="button" onClick={onBack} data-testid="activities-empty-reset" className={V3_SECONDARY}>Zurück</button>
      </V3Actions>
    </div>
  );
}

// M11 (+ M118): list from GET /member/activities; search and access filter live in the URL and only narrow that list.
export default function Activities() {
  const { member } = useMemberAuth();
  const { search } = useLocation();
  const navigate = useNavigate();
  const f = readActivityFilter(search);
  const [q, setQ] = useState(f.q);
  const res = useActivities();
  const n = accessChoiceCount(f);
  const list = filterActivities(res.data || [], { ...f, q });
  return (
    <div data-m="M11" data-testid="activities-page">
      <V3Title title="Aktivitäten entdecken" intro="Finde ein Treffen, das zeitlich und von den Zugangsangaben zu dir passt." />
      <SearchField id="activities-q" label="Aktivitäten durchsuchen" placeholder="Aktivität oder öffentlichen Ort suchen …" value={q} onChange={setQ} testid="activities-search" />
      <FilterBar testid="member-activity-filters" end={(n > 0 || q) && <Link to="/aktivitaeten" onClick={() => setQ("")} data-testid="activities-filter-reset" className="inline-flex min-h-[44px] items-center px-[12px] text-[13px] font-bold text-v3-magenta hover:underline">Zurücksetzen</Link>}>
        {member?.city && <Pill tone="ink" testid="activities-city">{member.city}</Pill>}
        <Link to={`/aktivitaeten/zugang${activityQuery({ ...f, q })}`} data-testid="activities-access-filter" className="inline-flex min-h-[44px] items-center lg:min-h-[29px]"><Pill tone="ink">{n ? `Zugangsangaben · ${n}` : "Zugangsangaben"}</Pill></Link>
        <Link to={`/aktivitaeten/karte${activityQuery({ ...f, q })}`} data-testid="activities-view-map" className="inline-flex min-h-[44px] items-center gap-1 px-2 text-[13px] font-bold text-v3-magenta hover:underline"><MapIcon className="h-4 w-4" aria-hidden="true" />Ortsübersicht</Link>
      </FilterBar>
      <div className="mt-[16px]">
        {res.error ? <ConnectionError onRetry={res.reload} /> : !res.data ? <Loading />
          : !res.data.length ? <p className={`${V3_CARD} px-[18px] py-[16px] text-[14px] text-v3-muted`} data-testid="activities-none">Gerade gibt es keine Aktivitäten. Schau später wieder vorbei oder lege selbst eine an.</p>
          : !list.length ? <NoMatch f={{ ...f, q }} city={member?.city} onBack={() => { setQ(""); navigate("/aktivitaeten"); }} />
          : <div className="grid grid-cols-1 gap-[18px] lg:grid-cols-2 lg:gap-x-[24px] lg:gap-y-[64px]" data-testid="member-activities-grid">{list.map((a) => <ActivityListCard key={a.id} a={a} />)}</div>}
      </div>
      <div className="mt-[22px] flex flex-wrap gap-2">
        <Link to="/aktivitaeten/neu" data-testid="activities-create" className={V3_GHOST}><Plus className="h-4 w-4" aria-hidden="true" />Aktivität erstellen</Link>
        <Link to="/aktivitaeten/verwalten" data-testid="activities-manage" className={V3_GHOST}><Settings2 className="h-4 w-4" aria-hidden="true" />Eigene verwalten</Link>
      </div>
    </div>
  );
}

const UnknownAside = () => <AsideCard icon={CircleHelp} title="Unbekannt bleibt offen" text="Eine ungeklärte Angabe ist keine Bestätigung der Zugänglichkeit." testid="activities-unknown-aside" />;

// M59: one choice per feature; "Unbekannte mit anzeigen" keeps activities whose feature is still unknown.
export function ActivityFilter() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(() => readActivityFilter(search));
  const set = (k) => (v) => setDraft((d) => { const access = { ...d.access }; if (v) access[k] = v; else delete access[k]; return { ...d, access }; });
  return (
    <div data-m="M59" data-testid="activities-filter-page">
      <V3Title title="Zugangsmerkmale filtern" intro="Unbekannte Angaben bleiben erkennbar und können bewusst mit angezeigt werden." testid="activities-filter-title" />
      <V3Columns layout="app" aside={<UnknownAside />} main={<>
        <div className="space-y-[11px]" data-testid="access-filter">
          {FILTER_FEATURES.map(([k, label]) => <SelectCard key={k} id={`access-filter-${k}`} title={label} value={draft.access[k] || ""} onChange={set(k)} options={ACCESS_CHOICES} testid={`access-filter-${k}`} />)}
        </div>
        <p className="mt-[12px] text-[13px] text-v3-muted">Die Angaben stammen von Gastgebenden oder Veranstaltern und sind keine Zertifizierung.</p>
        <V3Actions>
          <button type="button" onClick={() => navigate(`/aktivitaeten${activityQuery(draft)}`)} data-testid="access-filter-apply" className={V3_PRIMARY}>Filter anwenden</button>
          <Link to={`/aktivitaeten${search}`} data-testid="access-filter-back" className={V3_SECONDARY}>Zurück</Link>
        </V3Actions>
      </>} />
    </div>
  );
}

// M58: schematic overview without coordinates or an external map; the readable list stays the main view.
export function ActivityMap() {
  const { search } = useLocation();
  const res = useActivities();
  const entries = mapEntries(filterActivities(res.data || [], readActivityFilter(search)));
  return (
    <div data-m="M58" data-testid="activities-map">
      <V3Title title="Aktivitäten auf der Karte" intro="Die Ortsübersicht ergänzt die vollständig lesbare Ergebnisliste." testid="activities-map-title" />
      <V3Columns layout="app" aside={<><AreaCard text={entries[0] ? `${entries[0].place} · ${entries[0].day}` : "Keine Orte"} testid="activities-map-area" /><ResponsibleCard /></>} main={
        res.error ? <ConnectionError onRetry={res.reload} /> : !res.data ? <Loading /> : (<>
          <div aria-hidden="true" className="relative h-[240px] overflow-hidden rounded-[12px] border border-v3-line bg-v3-lilac lg:h-[380px]" data-testid="activities-map-schema">
            <svg className="absolute inset-0 h-full w-full text-v3-line" preserveAspectRatio="none" viewBox="0 0 100 100"><path d="M0 70 C30 60 45 80 100 55 M20 0 C25 40 40 60 35 100 M60 0 C55 30 70 70 90 100" fill="none" stroke="currentColor" strokeWidth="1.2" /></svg>
            {entries.map((e) => <span key={e.id} className="absolute flex h-[34px] w-[34px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-v3-magenta text-[15px] font-bold text-white shadow" style={{ left: `${e.x}%`, top: `${e.y}%` }}>{e.n}</span>)}
          </div>
          <p className="mt-[10px] text-[12.5px] text-v3-muted" data-testid="activities-map-note">Schematische Ortsübersicht · Keine Live-Karte · Positionen sind nicht ortsgenau</p>
          <div className="mt-[12px]"><DetailList>
            {entries.map((e) => <DetailRow key={e.id} label={`${e.n} · ${e.title}`} value={`${e.place} · ${e.day}`} to={`/aktivitaeten/${encodeURIComponent(e.id)}`} testid={`activities-map-entry-${e.id}`} />)}
            <DetailRow label="Alternative" value="Alle Ergebnisse als Liste öffnen" to={`/aktivitaeten${search}`} testid="activities-map-list" />
          </DetailList></div>
          {!entries.length && <p className="mt-[10px] text-[14px] text-v3-muted" data-testid="activities-map-empty">Keine Aktivitäten mit Ortsangabe.</p>}
        </>)} />
    </div>
  );
}

// M109: series need series_id (proposal V.5); without it nothing is grouped.
export function ActivitySeries() {
  const { id } = useParams();
  const res = useActivities();
  const list = seriesOf(res.data || [], id);
  return (
    <div data-m="M109" data-testid="activities-series">
      <V3Title title="Nächster Termin einer Serie" intro="Für jeden Serientermin entscheidest du über deine Teilnahme neu." testid="activities-series-title" />
      {res.error ? <ConnectionError onRetry={res.reload} /> : !res.data ? <Loading /> : list.length
        ? <div className="grid grid-cols-1 gap-[18px] lg:grid-cols-2" data-testid="activities-series-grid">{list.map((a) => <ActivityListCard key={a.id} a={a} />)}</div>
        : <Unavailable testid="activities-series-unavailable">Serien kennt der gemeinsame Core noch nicht. Jeder Termin erscheint einzeln in der Liste der Aktivitäten.</Unavailable>}
      <V3Actions className="mt-[21px]"><Link to="/aktivitaeten" className={V3_SECONDARY} data-testid="activities-series-back">Zu den Aktivitäten</Link></V3Actions>
    </div>
  );
}

